// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import RenderIf from 'components/if-statement';
import {
  ListContainer,
  LoaderContainer,
} from 'components/grid/list/components/style';
import { noop, get, omit, invert, orderBy, isFunction } from 'lodash';
import { set } from 'lodash/fp';

export const propTypes = {
  renderLayout: PropTypes.func,
  renderGridLayout: PropTypes.func,
  items: PropTypes.array,
  meta: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  sort: PropTypes.string,
  setSort: PropTypes.func,
  filter: PropTypes.object,
  search: PropTypes.any,
  setFilterKey: PropTypes.func,
  isSortSet: PropTypes.func,
  getSortValue: PropTypes.func,
  setFilter: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.array,
  ]),
  perPage: PropTypes.number,
  page: PropTypes.number,
  setPage: PropTypes.func,
  setPerPage: PropTypes.func,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

class List extends React.Component<any> {
  private listComponents;

  static propTypes = propTypes;

  static defaultProps = {
    sort: undefined,
    items: [],
    meta: {},
    error: '',
    setSort: undefined,
    isSortSet: undefined,
    getSortValue: undefined,
    filter: undefined,
    search: undefined,
    setFilterKey: undefined,
    setFilter: undefined,
    children: undefined,
    perPage: 10,
    page: 1,
    setPage: undefined,
    setPerPage: undefined,
    loading: undefined,
    renderLayout({
      filters,
      dataGrid,
      pagination,
      loader,
      loading,
      error,
      errorComponent,
      className,
    }) {
      return (
        <ListContainer className={className}>
          {filters}
          <React.Fragment>
            <RenderIf condition={error}>{errorComponent || null}</RenderIf>
            <RenderIf condition={loading && !error}>
              <LoaderContainer>{loader}</LoaderContainer>
            </RenderIf>
            <RenderIf condition={!loading && !error}>
              {this.props.renderGridLayout({ dataGrid })}
            </RenderIf>
          </React.Fragment>
          {pagination}
        </ListContainer>
      );
    },
    renderGridLayout({ dataGrid }) {
      return dataGrid;
    },
  };

  constructor(props) {
    super(props);

    this.listComponents = [
      'loader',
      'filtersComponent',
      'dataComponent',
      'paginationComponent',
      'errorComponent',
    ];
  }

  elementHasFlag = ({ type: Component }, flag) => Component.type === flag;

  setSort = (...params) => {
    const { setSort } = this.props;
    if (setSort) {
      return setSort.bind(this)(...params);
    }
    return this.nativeSort(...params);
  };

  nativeSort = (sort) => {
    const sortMap = { asc: 1, desc: -1 };

    const { keys, order } = Object.entries(sort).reduce(
      (config, [sortKey, order]) => {
        const sortOrder = invert(sortMap)[order];
        if (sortOrder) {
          return {
            keys: [...config.keys, sortKey],
            order: [...config.order, sortOrder],
          };
        }
        return config;
      },
      { keys: [], order: [] },
    );

    this.setState((state) => ({
      ...set(state, 'items', orderBy(get(state, 'items', keys, order))),
      sort,
    }));
  };

  getData = () => this.props.items;

  getError = () => this.props.error;

  paginationProps = (paginationProps) => {
    const {
      perPage,
      setPage,
      setPerPage,
      page: currentPage,
      meta,
    } = this.props;

    return {
      ...paginationProps,
      onPageChange: setPage,
      onPerPageChange: setPerPage,
      currentPage,
      perPage,
      count: get(meta, 'count', 0),
      totalCount: get(meta, 'totalCount', 0),
      totalPages: get(meta, 'totalPages', 0),
    };
  };

  assignListLayoutElements = (children) => {
    React.Children.forEach(
      children,
      (child) => {
        if (this.isTargetedElement(child)) {
          return this.processChild(child);
        }

        if (child && child.props && child.props.children) {
          return this.assignListLayoutElements(child.props.children);
        }
      },
      this,
    );
  };

  isTargetedElement = ({ type: Component }) => {
    return this.listComponents.reduce((hasFlag, flag) => {
      return hasFlag ? hasFlag : Component.type === flag;
    }, false);
  };

  processChild = (child) => {
    if (this.elementHasFlag(child, 'loader')) {
      this.loaderElement = child;
    }

    if (this.elementHasFlag(child, 'errorComponent')) {
      this.errorComponent = React.cloneElement(child, {
        error: this.getError(),
      });
    }

    if (this.elementHasFlag(child, 'filtersComponent')) {
      // Need to omit fields that are not actual filters
      const { filter, search, setFilter, setFilterKey = noop } = this.props;

      this.filterElement = React.cloneElement(child, {
        filter: { ...filter, search },
        setFilterKey: setFilterKey.bind(this),
        setFilter,
      });
    }

    const props = omit(child.props, ['children', 'key', 'ref']);

    if (this.elementHasFlag(child, 'dataComponent')) {
      const {
        sort,
        filter,
        isSortSet,
        getSortValue,
        setFilterKey = noop,
      } = this.props;

      this.dataElement = React.cloneElement(child, {
        ...props,
        sort,
        filter,
        isSortSet,
        getSortValue,
        setFilterKey: setFilterKey.bind(this),
        setSort: this.setSort,
        data: this.getData(),
      });
    }

    if (this.elementHasFlag(child, 'paginationComponent')) {
      this.paginationElement = React.cloneElement(child, {
        ...this.paginationProps(props),
        data: this.getData(),
      });
    }
  };

  render() {
    const { children, loading, className } = this.props;

    const content = isFunction(children)
      ? children(this.props)
      : React.Children.toArray(children);

    if (!content) {
      return null;
    }

    const { renderLayout } = this.props;

    this.assignListLayoutElements(content);

    const layoutProps = {
      data: this.getData(),
      error: this.getError(),
      loading,
      filters: this.filterElement,
      loader: this.loaderElement,
      dataGrid: this.dataElement,
      pagination: this.paginationElement,
      errorComponent: this.errorComponent,
      className,
    };

    return renderLayout.call(this, layoutProps);
  }
}

export default List;
